// NOTE: this contract is duplicated from CommandCenterMap-BE
// src/app/ws/updateMessage.ts (`DomainEventFrame`) — keep the two in sync
// until a shared package exists (TMDXNC-2 M2).
// SYNC CAVEAT: FE intentionally NARROWS `category` to the
// `'scheduled' | 'actual'` union (matching BE's real `EventCategory`). A sync
// pass must NOT widen it back to `string` to match BE's loosened wire type —
// the narrow union is load-bearing for the schema picklist and downstream
// scheduled/actual ghosting logic.

/** A ready-to-render domain event pushed by the BE `/ws/updates` gateway. */
export interface DomainEventFrame {
  kind: 'domainEvent';
  /** bigint PK as a string — the monotonic seq/cursor. Order & dedup by this. */
  id: string;
  type: string;
  category: 'scheduled' | 'actual';
  domain: string;
  severity: string;
  entityId: string;
  /**
   * Case identity, when the event belongs to a case (TMDXNC-37). `caseLabel` is
   * the case grouping key and the card label; `organ` drives the accent colour
   * and the organ legend chips. Both are absent — never null — on events with
   * no case (weather, most flight).
   * `toScenarioEvent` folds them into `ScenarioEvent.payload`, which is where
   * `buildLogEntry` reads them from.
   */
  caseLabel?: string;
  organ?: string;
  /** source/business event time, ISO 8601 */
  occurredAt: string;
  payload: Record<string, unknown>;
  media?: unknown[];
  pairKey?: string;
  spanKey?: string;
  spanRole?: 'open' | 'close';
  /** server-observed instant; NOT stable across live vs catch-up — never sort by this. */
  ts: number;
  /**
   * Client-only, never on the wire: when THIS client first inserted the frame
   * from the socket or since-seq catch-up. Absent on REST-loaded rows, which is
   * what keeps late-arrival grace off the backlog (TMDXNC-500 E012).
   */
  receivedAtMs?: number;
}

/** REST catch-up / lookback response from `GET api/events`. */
export interface DomainEventFeed {
  events: DomainEventFrame[];
  latestSeq: string;
  /**
   * Oldest instant the loaded log is complete back to, epoch ms (TMDXNC-54).
   * Absent until a lookback response has been merged — a sinceSeq response does
   * not carry one. Wall-clock, so positioning it needs `- t0Ms`, and the result
   * is legitimately negative (see the `offsetMs` sign contract).
   */
  historyFloorMs?: number;
  /** The BE row cap bound the response, so `historyFloorMs` is a truncation. */
  capped: boolean;
  /** Server retention ceiling, ms. Absent means "the BE did not say". */
  retentionMs?: number;
}

/**
 * Server-side relevance profile for `GET api/events?view=` (TMDXNC-54).
 * `undefined` is the unfiltered feed — the shape `/alerts-dashboard` needs,
 * since it renders a card per event across the whole vocabulary.
 *
 * SYNC CAVEAT: mirrors `FEED_VIEWS` in CommandCenterMap-BE
 * `src/services/domainEvents/feed.ts`. An unknown value is not an error there —
 * the BE logs it and serves the full feed — so a drift here degrades to "no
 * saving" rather than "no data".
 */
export type FeedView = 'case' | 'aircraft';

/**
 * Query arg for `getDomainEvents`. `view` alone forms the cache key (see
 * `serializeQueryArgs` in `domainEvents.api.ts`); `lookbackMs` widens the SAME
 * entry rather than minting a new one, and is the monotonic high-water mark
 * `EventsFeedProvider` maintains.
 */
export interface DomainEventFeedArg {
  view?: FeedView;
  lookbackMs?: number;
}

/**
 * Does a live frame belong to `view`? The FE mirror of the BE's `FEED_VIEWS`
 * predicate, needed because live WS frames bypass the REST filter entirely: a
 * `view=case` cache entry that accepted every pushed frame would refill with
 * exactly the rows the view exists to exclude.
 */
export const matchesFeedView = (
  frame: DomainEventFrame,
  view: FeedView | undefined,
): boolean => {
  if (view === undefined) {
    return true;
  }
  if (view === 'case') {
    // BE predicate: `caseLabel IS NOT NULL` (CommandCenterMap-BE
    // services/domainEvents/feed.ts, `FEED_VIEWS.case`). The wire contract
    // says caseLabel is absent (never null) on events with no case, so this
    // mirror also checks `!== ''` — stricter than the server's own
    // predicate, which the comments elsewhere in this domain warn against
    // (a mirror stricter than the server can reject a frame the BE served
    // into the view, which is permanent loss, not a retry). This ONE case is
    // safe ONLY because of a cross-repo dependency: the BE's frame
    // serializer (CommandCenterMap-BE services/domainEvents/frame.ts,
    // `...(row.caseLabel ? { caseLabel: row.caseLabel } : {})`) uses a FALSY
    // check, not a null check, so an empty-string `caseLabel` never actually
    // reaches the wire in the first place — `''` is unreachable here in
    // practice, not merely unlikely. If that serializer is ever changed to a
    // strict null check, this `!== ''` becomes the same permanent-loss trap
    // the comment above warns about, and must be revisited together with it.
    return frame.caseLabel !== undefined && frame.caseLabel !== '';
  }
  // 'aircraft' — BE predicate: `domain = 'flight'`, nothing more. Do NOT also
  // require entityId: the column is NOT NULL server-side, so the BE cannot
  // filter on it, and a mirror STRICTER than the server would reject a frame the
  // BE served into this view while the seq cursor advanced past it — permanent
  // loss, not a retry.
  return frame.domain === 'flight';
};

/**
 * Ascending order by numeric `id`. BigInt because the bigint PK exceeds
 * Number.MAX_SAFE_INTEGER and a lexicographic string sort would put '10'
 * before '9'.
 */
export const byNumericId = (
  a: DomainEventFrame,
  b: DomainEventFrame,
): number => {
  const ai = BigInt(a.id);
  const bi = BigInt(b.id);
  return ai < bi ? -1 : ai > bi ? 1 : 0;
};

/** Is `candidate` a later seq than `current`? An empty `current` is always behind. */
export const isLaterSeq = (candidate: string, current: string): boolean =>
  current === '' ? true : BigInt(candidate) > BigInt(current);
